import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Tooltip } from "antd";
import SingleLineField from "../SingleLineLongField";
import MultiLineField from "../MultiLineField";
import ImageUploadField from "../ImageUploadField";
import Tiptap from "./Tiptap";
import { articleHeadlineWarning, ARTICLE_HEADLINE_TOOLTIP } from "../../../utils/validators";

// Node types that count as real content on their own, even without any text
// (an image- or video-led article is perfectly valid).
const MEDIA_NODE_TYPES = new Set([
  "image",
  "video",
  "youtube",
  "factbox",
  "leadform",
]);

// Walks the whole TipTap document tree — not just the first two levels — and
// returns true as soon as it finds real, non-whitespace text or a media node.
// The old two-level check wrongly rejected content whose text lives deeper,
// such as bulleted/numbered lists and quotes.
export const hasMeaningfulContent = (node) => {
  if (!node) return false;

  if (typeof node.text === "string" && node.text.trim().length > 0) {
    return true;
  }

  if (MEDIA_NODE_TYPES.has(node.type)) {
    return true;
  }

  return Array.isArray(node.content) && node.content.some(hasMeaningfulContent);
};

// Own component so the useWatch subscription does not re-render the Tiptap editor on every keystroke.
const HeadlinePunctuationWarning = () => {
  const warning = articleHeadlineWarning(useWatch({ name: "headline" }));
  if (!warning) return null;
  return <p className="mt-1 text-sm text-yellow-600">{warning}</p>;
};

const ArticleContentEditor = ({ readOnly, allowSponsoredLinks }) => {
  const form = useFormContext();
  const { formState: { errors } } = form;

  return (
    <>
        <SingleLineField
          label="Article Title"
          htmlfor="headline"
          readOnly={readOnly}
          required={true}
          tooltip={ARTICLE_HEADLINE_TOOLTIP}
          top
        />
        {errors.headline && (
          <p className="mt-1 text-sm text-red-600">Article Title is required</p>
        )}
        <HeadlinePunctuationWarning />

        <SingleLineField
          label="Author Name"
          htmlfor="articleByline"
          readOnly={readOnly}
          required={true}
        />
        {errors.articleByline && (
          <p className="mt-1 text-sm text-red-600">Author Name is required</p>
        )}

        <MultiLineField
          label="Subtitle"
          htmlfor="teaser"
          readOnly={readOnly}
          required={true}
          rows={3}
        />
        {errors.teaser && (
          <p className="mt-1 text-sm text-red-600">Subtitle is required</p>
        )}

        <ImageUploadField
          label="Cover Image"
          name="coverImage"
          readOnly={readOnly} />

        <SingleLineField
          label="Photo Credit (Needs to be activated in template)"
          htmlfor="imageByline"
          readOnly={readOnly}
        />

        <div>
          <Tooltip title="Required field">
            <label
              htmlFor="content"
              className="block text-sm font-medium text-gray-700"
            >
              Main Content <span className="text-red-500">*</span>
            </label>
          </Tooltip>
          <div className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 mt-1 block w-full sm:text-sm border rounded-md ${
            errors.contentObject ? 'border-red-500' : 'border-gray-300'
          }`}>
            <div>
              <Controller
                name="contentObject"
                control={form.control}
                rules={{
                  required: 'Main Content is required',
                  validate: (value) => {
                    if (!value || !value.content || value.content.length === 0) {
                      return 'Main Content is required';
                    }
                    if (!hasMeaningfulContent(value)) {
                      return 'Main Content cannot be empty';
                    }
                    return true;
                  }
                }}
                render={({
                  field: { onChange, value }
                }) => (
                  <Tiptap
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                    allowSponsoredLinks={allowSponsoredLinks}
                  />
                )}
              />
            </div>
          </div>
          {errors.contentObject && (
            <p className="mt-1 text-sm text-red-600">{errors.contentObject.message}</p>
          )}
        </div>
      

    </>
  );
};

export default ArticleContentEditor;
